<template>
  <div class="container">
    <BasicTitle :title="pageTitle" />
    <BasicSubtitle subtitle="Los campos marcados con (*) son obligatorios." />
    <div class="container-form">
      <BasicLabel label="Apartamento *" />
      <BorderSelect
        v-model="form.apartmentId"
        label="Apartamento *"
        :options="apartmentOptions"
        placeholder="Seleccione"
      />
      <FormError :show="Boolean(errors.apartmentId)" :message="errors.apartmentId" />

      <BasicLabel label="Fecha *" />
      <BorderInput
        v-model="form.date"
        label="Fecha *"
        type="date"
        :max="maxDate"
        @change="validateDate"
      />
      <FormError :show="Boolean(errors.date)" :message="errors.date" />

      <BasicLabel label="Moneda *" />
      <BorderSelect
        v-model="form.currencyId"
        label="Moneda *"
        :options="currencyOptions"
        placeholder="Seleccione"
      />
      <FormError :show="Boolean(errors.currencyId)" :message="errors.currencyId" />

      <BasicLabel label="Monto *" />
      <BorderInput
        v-model="form.amount"
        label="Monto *"
        type="number"
        step="0.01"
        min="0"
        @keyup.enter="onSave"
      />
      <FormError :show="Boolean(errors.amount)" :message="errors.amount" />

      <BasicLabel label="Concepto *" />
      <BorderSelect
        v-model="form.conceptId"
        label="Concepto *"
        :options="conceptOptions"
        placeholder="Seleccione"
      />
      <FormError :show="Boolean(errors.conceptId)" :message="errors.conceptId" />

      <BasicLabel label="Cuenta contable *" />
      <BorderSelect
        v-model="form.accountId"
        label="Cuenta contable *"
        :options="accountOptions"
        placeholder="Seleccione"
      />
      <FormError :show="Boolean(errors.accountId)" :message="errors.accountId" />

      <BasicLabel label="Detalle" />
      <BorderInput v-model="form.detail" label="Detalle" type="textarea" />

      <BasicLabel label="Adjuntar comprobante" />
      <div class="file-upload">
        <input
          :id="fineUploadId"
          ref="attachmentInput"
          class="file-upload__input"
          type="file"
          accept="image/*,.pdf"
          @change="onAttachmentChange"
        />
        <label class="file-upload__label" :for="fineUploadId">
          <span class="file-upload__icon pi pi-upload" aria-hidden="true"></span>
          <div class="file-upload__text">
            <span class="file-upload__title">Haz clic para seleccionar tus comprobantes</span>
            <span class="file-upload__hint">Archivos PDF o imágenes</span>
          </div>
        </label>
      </div>
      <p class="file-upload__helper">El archivo se guardará al confirmar la multa.</p>

      <div v-if="attachmentFile" class="file-upload__section">
        <p class="file-upload__subtitle">Archivo pendiente por subir</p>
        <ul class="file-upload__list">
          <li class="file-upload__list-item">
            <span class="file-upload__filename">{{ attachmentFile.displayName }}</span>
            <div class="file-upload__meta">
              <span class="file-upload__tag">Pendiente</span>
              <button
                type="button"
                class="file-upload__action file-upload__action--danger"
                @click="clearAttachmentSelection"
              >
                Quitar
              </button>
            </div>
          </li>
        </ul>
      </div>

      <div v-if="form.attachmentUrl" class="file-upload__section">
        <p class="file-upload__subtitle">Comprobante guardado</p>
        <ul class="file-upload__list">
          <li class="file-upload__list-item">
            <span class="file-upload__filename">{{ storedAttachmentLabel }}</span>
            <div class="file-upload__meta">
              <button type="button" class="file-upload__action" @click="handleStoredAttachmentClick">
                Ver
              </button>
              <button
                type="button"
                class="file-upload__action file-upload__action--danger"
                @click="removeStoredAttachment"
              >
                Eliminar
              </button>
            </div>
          </li>
        </ul>
      </div>

      <div class="notify">
        <input id="notify" type="checkbox" v-model="form.notifyResident" />
        <label for="notify">Notificar al residente</label>
      </div>
    </div>
    <PrimaryButton :label="primaryButtonLabel" :click="onSave" />
  </div>
</template>

<script>
import { computed, reactive, watch, onMounted, ref } from "vue";
import { useRouter } from "vue-router";
import store from "../../store";
import BasicTitle from "../../widgets/title/BasicTitle";
import BasicSubtitle from "../../widgets/subtitle/BasicSubtitle";
import BasicLabel from "../../widgets/label/BasicLabel";
import BorderInput from "../../widgets/input/BorderInput";
import BorderSelect from "../../widgets/select/BorderSelect";
import FormError from "../../widgets/error/FormError";
import PrimaryButton from "../../widgets/button/PrimaryButton";
import { openMode, actions, aws } from "../../constants";
import { toOptionList } from "../../utils/options";
import { sortAccountsByCode } from "../../utils/accounting/accounts.mjs";

const createEmptyErrors = () => ({
  apartmentId: "",
  date: "",
  currencyId: "",
  amount: "",
  conceptId: "",
  accountId: "",
});

export default {
  components: {
    BasicTitle,
    BasicSubtitle,
    BasicLabel,
    BorderInput,
    BorderSelect,
    FormError,
    PrimaryButton,
  },
  setup() {
    const router = useRouter();

    const selectedBuilding = computed(() => store.state.general.selectedBuilding);
    const buildingId = computed(() => {
      const value = selectedBuilding.value;
      if (!value) return null;
      return typeof value === "object" ? value.id : value;
    });

    const isEditMode = computed(() => store.state.openMode === openMode.MODIFY);
    const pageTitle = computed(() => (isEditMode.value ? "Editar multa" : "Nueva multa"));
    const primaryButtonLabel = computed(() => (isEditMode.value ? "Actualizar" : "Guardar"));
    const maxDate = computed(() => new Date().toISOString().slice(0, 10));

    const form = reactive({
      id: null,
      buildingId: null,
      apartmentId: "",
      date: "",
      currencyId: "",
      amount: "",
      conceptId: "",
      accountId: "",
      attachmentUrl: "",
      detail: "",
      notifyResident: false,
    });

    const errors = reactive(createEmptyErrors());
    const attachmentInput = ref(null);
    const fineUploadId = "fine-upload-input";
    const attachmentFile = ref(null);

    const apartmentOptions = computed(() =>
      toOptionList(store.state.general.apartments, {
        labelFn: (item) => item.name || item.number || item.code || String(item.id),
      })
    );

    const conceptOptions = computed(() =>
      toOptionList(store.state.concept.concepts, {
        labelFn: (item) => item.name || item.description || String(item.id),
      })
    );

    const fineAccounts = computed(() =>
      sortAccountsByCode(
        (store.state.account.accounts || []).filter(
          (item) => item?.type === "expense"
        )
      )
    );

    const accountOptions = computed(() =>
      toOptionList(fineAccounts.value, {
        labelFn: (item) =>
          `${item.code ? `${item.code} - ` : ""}${item.name || item.description || item.code}`,
      })
    );

    const currencyOptions = computed(() =>
      toOptionList(store.state.general.currencies, {
        labelFn: (item) => [item.code, item.name].filter(Boolean).join(" - "),
      })
    );

    const selectedFineId = computed(
      () => store.state.fine.selectedFines?.[0]?.id || store.state.fine.current?.id || null
    );

    const notify = (type, message) => {
      if (!message) return;
      store.commit("setToast", { type, message });
    };

    const storedAttachmentLink = computed(() => form.attachmentUrl?.trim() || "");
    const storedAttachmentLabel = computed(() => {
      if (!storedAttachmentLink.value) return "Archivo";
      const segments = storedAttachmentLink.value.split("/");
      return segments[segments.length - 1] || "Archivo";
    });

    const clearAttachmentSelection = () => {
      attachmentFile.value = null;
      if (attachmentInput.value) {
        attachmentInput.value.value = "";
      }
    };

    const onAttachmentChange = (event) => {
      const file = event?.target?.files?.[0];
      if (!file) return;
      const isValidType =
        file.type === "application/pdf" || file.type.startsWith("image/");
      if (!isValidType) {
        notify("warn", "Solo se permiten imágenes o archivos PDF.");
        event.target.value = "";
        return;
      }
      const extension = file.name.includes(".")
        ? file.name.slice(file.name.lastIndexOf("."))
        : "";
      const safeName = `multa-${Date.now()}-${Math.random()
        .toString(36)
        .slice(2, 8)}`;
      attachmentFile.value = {
        file,
        displayName: file.name,
        fileName: safeName,
        fileExt: extension,
      };
    };

    const removeStoredAttachment = () => {
      form.attachmentUrl = "";
    };

    const handleStoredAttachmentClick = () => {
      const link = storedAttachmentLink.value;
      if (!link) return;
      if (/^https?:\/\//i.test(link)) {
        window.open(link, "_blank", "noopener,noreferrer");
        return;
      }
      aws.downloadFile(storedAttachmentLabel.value, link, (_, error) => {
        if (error) {
          notify("error", "No fue posible descargar el comprobante.");
        }
      });
    };

    const buildAttachmentFolderName = () => {
      const base = form.date || new Date().toISOString().slice(0, 10);
      return `Multas-${base.replace(/-/g, "")}`;
    };

    const uploadAttachment = async () => {
      if (!attachmentFile.value) {
        return storedAttachmentLink.value || null;
      }
      if (!buildingId.value) {
        const message = "Seleccione un edificio antes de adjuntar un comprobante.";
        notify("error", message);
        alert(message);
        return null;
      }
      const response = await store.dispatch(actions.documentActions.getPath, {
        buildingId: buildingId.value,
        folderName: buildAttachmentFolderName(),
        documentTypeName: "Multas",
      });
      if (!response?.ok || !response?.path) {
        notify("error", response?.message || "No fue posible preparar la carpeta para adjuntos.");
        return null;
      }
      const key = `${response.path}${attachmentFile.value.fileName}${attachmentFile.value.fileExt}`;
      try {
        await new Promise((resolve, reject) => {
          aws.uploadFile(key, attachmentFile.value.file, (data, error) => {
            if (error || !data) {
              reject(error || new Error("No fue posible subir el archivo."));
              return;
            }
            resolve(data);
          });
        });
      } catch (error) {
        console.error(error);
        const message = "No fue posible subir el comprobante.";
        notify("error", message);
        alert(message);
        clearAttachmentSelection();
        return null;
      }
      clearAttachmentSelection();
      return key;
    };

    const clearErrors = () => {
      Object.keys(errors).forEach((key) => {
        errors[key] = "";
      });
    };

    const resetForm = () => {
      form.id = null;
      form.buildingId = buildingId.value || null;
      form.apartmentId = "";
      form.date = maxDate.value;
      form.currencyId = "";
      form.amount = "";
      form.conceptId = "";
      form.accountId = "";
      form.attachmentUrl = "";
      form.detail = "";
      form.notifyResident = false;
      clearErrors();
      clearAttachmentSelection();
    };

    const populateForm = (fine) => {
      if (!fine) return;
      form.id = fine.id ?? null;
      form.buildingId = fine.buildingId ?? buildingId.value ?? null;
      form.apartmentId = fine.apartmentId ? String(fine.apartmentId) : "";
      form.date = fine.date || maxDate.value;
      form.currencyId = fine.currencyId ? String(fine.currencyId) : "";
      form.amount = fine.amount !== undefined && fine.amount !== null ? String(fine.amount) : "";
      form.conceptId = fine.conceptId ? String(fine.conceptId) : "";
      form.accountId = fine.accountId ? String(fine.accountId) : "";
      form.detail = fine.detail || "";
      form.notifyResident = Boolean(fine.notifyResident);
      form.attachmentUrl = fine.attachmentUrl || "";
      clearAttachmentSelection();
      clearErrors();
    };

    const ensureGeneralData = async () => {
      if (!store.state.general.currencies?.length) {
        await store.dispatch("GENERAL_DATA");
      }
    };

    const loadCatalogs = async (id) => {
      if (!id) return;
      const requests = [
        store.dispatch(actions.apartmentActions.findAll, { buildingId: id }),
        store.dispatch(actions.accountActions.findAll, { buildingId: id }),
        store.dispatch(actions.conceptActions.findAll, { buildingId: id, type: "expense" }),
        store.dispatch(actions.commonExpenseActions.findAllClosure, { buildingId: id }),
      ];
      const results = await Promise.allSettled(requests);
      const rejected = results.find(
        (result) =>
          result.status === "rejected" ||
          (result.value && typeof result.value === "object" && result.value.ok === false)
      );
      if (rejected) {
        notify("error", "No fue posible cargar los catalogos requeridos.");
      }
    };

    const loadFine = async () => {
      if (!isEditMode.value || !selectedFineId.value) {
        resetForm();
        return;
      }
      const response = await store.dispatch(actions.fineActions.findOne, selectedFineId.value);
      if (response?.ok) {
        populateForm(response.data);
      } else {
        notify("error", response?.message || "No fue posible cargar la multa seleccionada.");
        router.push("/contabilidad/multa/listado");
      }
    };

    const isDateAllowed = (dateStr) => {
      if (!dateStr) return false;
      const selected = new Date(dateStr);
      if (Number.isNaN(selected.getTime())) return false;
      const today = new Date(maxDate.value);
      if (selected > today) return false;
      const closures = store.state.commonExpense?.closures || [];
      const month = selected.getUTCMonth() + 1;
      const year = selected.getUTCFullYear();
      const blocked = closures.some(
        (closure) =>
          Number(closure.buildingId) === Number(buildingId.value) &&
          Number(closure.month) === month &&
          Number(closure.year) === year &&
          closure.status === "closed"
      );
      return !blocked;
    };

    const validate = () => {
      clearErrors();
      if (!form.apartmentId) {
        errors.apartmentId = "Seleccione un apartamento";
      }
      if (!form.date) {
        errors.date = "Seleccione una fecha";
      } else if (!isDateAllowed(form.date)) {
        errors.date = "La fecha no es valida para el edificio";
      }
      if (!form.currencyId) {
        errors.currencyId = "Seleccione una moneda";
      }
      const amountValue = Number(form.amount);
      if (!Number.isFinite(amountValue) || amountValue <= 0) {
        errors.amount = "Ingrese un monto mayor a cero";
      }
      if (!form.conceptId) {
        errors.conceptId = "Seleccione un concepto";
      }
      if (!form.accountId) {
        errors.accountId = "Seleccione una cuenta contable";
      }
      return !Object.values(errors).some(Boolean);
    };

    const validateDate = () => {
      if (!form.date) {
        errors.date = "Seleccione una fecha";
      } else if (!isDateAllowed(form.date)) {
        errors.date = "La fecha no es valida para el edificio";
      } else {
        errors.date = "";
      }
    };

    const buildPayload = () => {
      const amountValue = Number(form.amount);
      return {
        id: form.id,
        buildingId: buildingId.value,
        apartmentId: Number(form.apartmentId),
        date: form.date,
        currencyId: Number(form.currencyId),
        amount: Number.isFinite(amountValue) ? amountValue : 0,
        conceptId: Number(form.conceptId),
        accountId: Number(form.accountId),
        detail: form.detail?.trim() || "",
        notifyResident: Boolean(form.notifyResident),
        attachmentUrl: form.attachmentUrl?.trim() || null,
      };
    };

    const onSave = async () => {
      if (!validate()) return;
      if (!buildingId.value) {
        notify("error", "Seleccione un edificio para continuar");
        return;
      }

      store.commit("setLoading", true);
      try {
        const hadPendingAttachment = Boolean(attachmentFile.value);
        const uploadedKey = await uploadAttachment();
        if (hadPendingAttachment && !uploadedKey) {
          const warnMessage =
            "No se pudo subir el comprobante. Se guardara la multa sin adjunto.";
          notify("warn", warnMessage);
          alert(warnMessage);
        }
        if (uploadedKey) {
          form.attachmentUrl = uploadedKey;
        }
        const payload = buildPayload();
        let response;
        if (isEditMode.value && form.id) {
          response = await store.dispatch(actions.fineActions.update, payload);
        } else {
          response = await store.dispatch(actions.fineActions.create, payload);
        }
        if (response?.ok) {
          notify(
            "success",
            isEditMode.value ? "Multa actualizada correctamente." : "Multa creada correctamente."
          );
          store.commit("setSelectedFines", []);
          router.push("/contabilidad/multa/listado");
        } else {
          notify("error", response?.message || "No fue posible guardar la multa.");
        }
      } catch (error) {
        console.error(error);
        notify("error", "Ocurrio un error al guardar la multa.");
      } finally {
        store.commit("setLoading", false);
      }
    };

    onMounted(async () => {
      store.commit("setLoading", true);
      try {
        await ensureGeneralData();
        if (buildingId.value) {
          await loadCatalogs(buildingId.value);
        }
        if (isEditMode.value) {
          await loadFine();
        } else {
          resetForm();
        }
      } finally {
        store.commit("setLoading", false);
      }
    });

    watch(buildingId, async (value, oldValue) => {
      if (value && value !== oldValue) {
        store.commit("setLoading", true);
        try {
          await loadCatalogs(value);
          if (!isEditMode.value) {
            resetForm();
            form.buildingId = value;
          }
        } finally {
          store.commit("setLoading", false);
        }
      }
    });

    watch(
      () => form.apartmentId,
      () => {
        if (form.apartmentId) errors.apartmentId = "";
      }
    );

    watch(
      () => form.currencyId,
      () => {
        if (form.currencyId) errors.currencyId = "";
      }
    );

    watch(
      () => form.amount,
      () => {
        const amountValue = Number(form.amount);
        if (form.amount && Number.isFinite(amountValue) && amountValue > 0) {
          errors.amount = "";
        }
      }
    );

    watch(
      () => form.conceptId,
      () => {
        if (form.conceptId) errors.conceptId = "";
      }
    );

    watch(
      () => form.accountId,
      () => {
        if (form.accountId) errors.accountId = "";
      }
    );

    watch(
      () => form.date,
      () => {
        if (form.date) validateDate();
      }
    );

    return {
      store,
      form,
      errors,
      pageTitle,
      primaryButtonLabel,
      maxDate,
      apartmentOptions,
      conceptOptions,
      accountOptions,
      currencyOptions,
      validateDate,
      onSave,
      attachmentInput,
      attachmentFile,
      storedAttachmentLabel,
      onAttachmentChange,
      clearAttachmentSelection,
      removeStoredAttachment,
      handleStoredAttachmentClick,
      fineUploadId,
    };
  },
};
</script>

<style scoped>
.container {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.container-form {
  display: flex;
  flex-direction: column;
  align-items: center;
  margin: 2rem 0;
  width: 100%;
  max-width: 600px;
}

.container-form > * {
  width: 100%;
  margin-bottom: 1.5rem;
}

.container-form > *:last-child {
  margin-bottom: 0;
}

.notify {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

</style>

































